import "./LinearModelDefiner.css";

import { useRef, useState } from "react";

import { ComponentPlaceholder } from "../../../../../components/componentPlaceholder/componentPlaceholder";
import { ComponentTitle } from "../../../../../components/componentTitle/componentTitle";
import { FactorSelector } from "./FactorSelector";
import PropTypes from "prop-types";
import { CfSelect as ReactSelect } from "../../../../../components/CfSelect";
import { TraitBadge } from "../../../../../components/Badges/TraitBadge/TraitBadge";
import { TraitSelector } from "../../../../components/traitSelector/traitSelector";
import { components } from "react-select";
import { useClickAway } from "react-use";
import { useSelector } from "react-redux";

const SearchLabel = ({ data }) => (
  <>
    <i
      className={`fa-solid fa-flask me-1 ${data.value ? "" : "discrete-icon"}`}
    />
    {data.label}
  </>
);

// Inside react-select's own SingleValue: v5 lays it over the input in a grid
// cell, a bare label fell to a second line.
const SingleValueLabel = (props) => (
  <components.SingleValue {...props}>
    <SearchLabel data={props.data} />
  </components.SingleValue>
);

/* Define linear model for ANOVA
 * by defining list of factors
 * Factors is an array of strings that can be either simple or interaction factors
 * Interaction factors are splitted by "*"
 * ANOVA's Response trait is resultMap.selectedTrait
 */
export const LinearModelDefiner = ({
  factors,
  setFactors,
  experimentsOptions,
  selectedExperiment,
  setSelectedExperiment,
}) => {
  const [trial, selectedTrait] = useSelector(({ resultMap }) => [
    resultMap.trial,
    resultMap.selectedTrait,
  ]);

  const [traitSelectorIsOpen, setTraitSelectorIsOpen] = useState(false);

  // Close trait selection on click away
  const traitSelectorRef = useRef(null);
  useClickAway(traitSelectorRef, () => setTraitSelectorIsOpen(false));

  return (
    <div className="powerdash-component linear-model-definer d-flex flex-column gap-2">
      <ComponentTitle
        title="Model Definition"
        topRightContent={
          factors.length > 0 && (
            <span
              className="clickable clear-button p-1"
              onClick={() => {
                setFactors([]);
              }}
            >
              <i className="fa-solid fa-arrow-rotate-left discrete-icon me-1" />{" "}
              Reset
            </span>
          )
        }
      />
      {trial ? (
        <>
          {experimentsOptions.length > 1 && (
            <ReactSelect
              className="select-experiment"
              options={experimentsOptions}
              onChange={setSelectedExperiment}
              value={selectedExperiment}
              placeholder="Select experiment"
              components={{
                Option: ({ innerProps, data, selectProps }) => (
                  <div className="p-2 clickable" {...innerProps}>
                    <SearchLabel
                      data={data}
                      inputValue={selectProps.inputValue}
                    />
                  </div>
                ),
                SingleValue: SingleValueLabel,
              }}
            />
          )}

          <div className="response-box d-flex flex-column gap-2 p-2">
            <span className="font-weight-bold title">Response</span>
            <div ref={traitSelectorRef}>
              {Object.keys(selectedTrait).length > 0 && (
                <TraitBadge
                  className="flex-grow-1"
                  trait={selectedTrait}
                  onClick={() => setTraitSelectorIsOpen(true)}
                />
              )}
              {traitSelectorIsOpen && <TraitSelector />}
            </div>
          </div>

          <span className="font-weight-bold title">Factors</span>
          <div className="factors d-flex flex-column gap-2">
            {factors.map((factor, index) => (
              <FactorSelector
                factor={factor}
                key={index}
                onChange={(value) =>
                  setFactors(factors.toSpliced(index, 1, value))
                }
                onRemove={() => setFactors(factors.toSpliced(index, 1))}
              />
            ))}

            <div
              className="add-factor d-flex align-items-center p-2 clickable"
              onClick={() => setFactors([...factors, null])}
            >
              <i className="fa-solid fa-plus fa-lg" /> Add factor
            </div>
          </div>
        </>
      ) : (
        <ComponentPlaceholder icon="fa-solid fa-flask-vial" />
      )}
    </div>
  );
};

LinearModelDefiner.propTypes = {
  factors: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  setFactors: PropTypes.func.isRequired,
  experimentsOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
    }).isRequired
  ).isRequired,
  selectedExperiment: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
  }),
  setSelectedExperiment: PropTypes.func.isRequired,
};
